import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  User,
  Bell,
  Globe,
  Shield,
  Trash2,
  Save,
  Eye,
  EyeOff,
  Mail,
  Volume2,
  FileUser
} from 'lucide-react';
import Card from '../components/ui/Card';
import Button from '../components/ui/Button';
import ProfileSetupContent from '../components/profile/ProfileSetupContent';
import { useAuth } from '../context/AuthContext';
import { settingsAPI } from '../services/api';
import toast from 'react-hot-toast';

const SettingsPage = () => {
  const location = useLocation();
  const { user, logout, updateUser, refreshUser } = useAuth();
  const [activeTab, setActiveTab] = useState(location.pathname === '/profile' ? 'profile' : 'personal-info');
  const [settings, setSettings] = useState({
    // Profile Settings
    name: '',
    email: '',
    phone: '',
    location: '',
    preferredLanguage: 'en',
    learningMode: 'balanced',
    careerGoal: '',
    experienceLevel: 'beginner',
    passingScore: 70,

    // 🔥 EXTENDED PERSONAL INFO FIELDS
    fatherName: '',
    motherName: '',
    dateOfBirth: '',
    nationality: '',
    address: '',
    linkedin: '',
    github: '',
    website: '',
    bio: '',

    // 🔥 EDUCATION FIELDS
    degree: '',
    institution: '',
    graduationYear: '',
    grade: '',

    // Notification Settings
    emailNotifications: true,
    weeklyProgress: true,
    achievementAlerts: true,

    // Account Settings
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  const [isLoading, setIsLoading] = useState(false);
  const [initialLoad, setInitialLoad] = useState(true);
  const [savedCareerGoal, setSavedCareerGoal] = useState('');
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false
  });

  // 🔥 FETCH USER SETTINGS ON COMPONENT LOAD
  useEffect(() => {
    setActiveTab(location.pathname === '/profile' ? 'profile' : 'personal-info');
  }, [location.pathname]);

  useEffect(() => {
    const fetchUserSettings = async () => {
      if (!user) return;

      try {
        setInitialLoad(true);
        console.log('📊 Fetching user settings for:', user.name);

        const response = await settingsAPI.getSettings();
        console.log('✅ Settings fetched successfully:', response);

        // 🔥 UPDATE SETTINGS STATE WITH FETCHED DATA
        setSettings(prevSettings => ({
          ...prevSettings,
          // Basic Profile
          name: response.profile?.name || user.name || '',
          email: response.profile?.email || user.email || '',
          phone: response.profile?.phone || '',
          location: response.profile?.location || '',
          preferredLanguage: response.profile?.preferred_language || 'en',
          careerGoal: response.profile?.career_goal || '',
          experienceLevel: response.profile?.experience_level || 'beginner',
          learningMode: response.profile?.learning_mode || 'balanced',
          passingScore: response.learning?.passing_score ?? 70,

          // 🔥 EXTENDED PERSONAL INFO
          fatherName: response.profile?.father_name || '',
          motherName: response.profile?.mother_name || '',
          dateOfBirth: response.profile?.date_of_birth || '',
          nationality: response.profile?.nationality || '',
          address: response.profile?.address || '',
          linkedin: response.profile?.linkedin || '',
          github: response.profile?.github || '',
          website: response.profile?.website || '',
          bio: response.profile?.bio || '',

          // 🔥 EDUCATION
          degree: response.profile?.degree || '',
          institution: response.profile?.institution || '',
          graduationYear: response.profile?.graduation_year || '',
          grade: response.profile?.grade || '',

          // Notifications
          emailNotifications: response.notifications?.email_notifications ?? true,
          weeklyProgress: response.notifications?.weekly_progress ?? true,
          achievementAlerts: response.notifications?.achievement_alerts ?? true,
        }));
        setSavedCareerGoal(response.profile?.career_goal || '');

        console.log('✅ Settings state updated with fetched data');

      } catch (error) {
        console.error('❌ Failed to fetch user settings:', error);
        toast.error('Failed to load your settings');
      } finally {
        setInitialLoad(false);
      }
    };

    fetchUserSettings();
  }, [user]);





  

  // 🔥 HANDLE SETTING CHANGES WITH AUTO-SAVE
  const handleSettingChange = (key, value) => {
    console.log(`🔧 Setting changed: ${key} = ${value}`);

    const nextSettings = {
      ...settings,
      [key]: value
    };

    setSettings(nextSettings);

    if (key === 'careerGoal') {
      return;
    }

    // 🔥 AUTO-SAVE AFTER 2 SECONDS (DEBOUNCE)
    clearTimeout(window.settingsSaveTimeout);
    window.settingsSaveTimeout = setTimeout(() => {
      saveSettingToBackend(key, value, nextSettings);
    }, 2000);
  };

  const handleLearningPathRefresh = async (snapshot, result) => {
    const nextCareerGoal = snapshot.careerGoal?.trim() || '';
    const roadmapId = result?.data?.repersonalization?.roadmap_id;

    localStorage.removeItem('userRoadmap');
    localStorage.removeItem('roadmapId');
    localStorage.setItem('learningPathUpdatedAt', new Date().toISOString());

    updateUser({
      name: snapshot.name,
      email: snapshot.email,
      career_goal: nextCareerGoal,
      careerGoal: nextCareerGoal,
    });

    try {
      await refreshUser();
    } catch (error) {
      console.error('Failed to refresh user after career goal update:', error);
    }

    setSavedCareerGoal(nextCareerGoal);

    window.dispatchEvent(new CustomEvent('learning-path-updated', {
      detail: {
        careerGoal: nextCareerGoal,
        roadmapId,
      }
    }));
  };

  // 🔥 SAVE SPECIFIC SETTING TO BACKEND
  const saveSettingToBackend = async (changedKey, changedValue, snapshot = settings) => {
    try {
      setIsLoading(true);
      console.log(`💾 Auto-saving ${changedKey}: ${changedValue}`);

      // Determine which category the changed setting belongs to
      let updateData = {};
      let endpoint = '';

      // 🔥 PROFILE SETTINGS
      if (['name', 'email', 'phone', 'location', 'preferredLanguage', 'careerGoal', 
           'experienceLevel', 'learningMode', 'fatherName', 'motherName', 
           'dateOfBirth', 'nationality', 'address', 'linkedin', 'github', 
           'website', 'bio', 'degree', 'institution', 'graduationYear', 'grade'].includes(changedKey)) {

        updateData = {
          name: snapshot.name,
          email: snapshot.email,
          phone: snapshot.phone,
          location: snapshot.location,
          preferred_language: snapshot.preferredLanguage,
          career_goal: snapshot.careerGoal,
          experience_level: snapshot.experienceLevel,
          learning_mode: snapshot.learningMode,
          father_name: snapshot.fatherName,
          mother_name: snapshot.motherName,
          date_of_birth: snapshot.dateOfBirth,
          nationality: snapshot.nationality,
          address: snapshot.address,
          linkedin: snapshot.linkedin,
          github: snapshot.github,
          website: snapshot.website,
          bio: snapshot.bio,
          degree: snapshot.degree,
          institution: snapshot.institution,
          graduation_year: snapshot.graduationYear,
          grade: snapshot.grade
        };
        endpoint = 'profile';
      }

      // 🔥 NOTIFICATION SETTINGS
      else if (['emailNotifications', 'weeklyProgress', 
                'newFeatures', 'marketingEmails', 'lessonReminders', 'achievementAlerts'].includes(changedKey)) {

        updateData = {
          email_notifications: snapshot.emailNotifications,
          weekly_progress: snapshot.weeklyProgress,
          achievement_alerts: snapshot.achievementAlerts
        };
        endpoint = 'notifications';
      }
      else if (['passingScore'].includes(changedKey)) {
        updateData = {
          passing_score: Number(snapshot.passingScore) || 70
        };
        endpoint = 'learning';
      }
      if (!endpoint) {
        setIsLoading(false);
        return;
      }
      // Call the appropriate API endpoint
      const result = await settingsAPI.updateSettings(endpoint, updateData);
      console.log(`✅ ${endpoint} settings saved:`, result);

      const repersonalizationTriggered = Boolean(result?.data?.repersonalization?.triggered);
      if (endpoint === 'profile') {
        updateUser({
          name: snapshot.name,
          email: snapshot.email,
          career_goal: snapshot.careerGoal,
          careerGoal: snapshot.careerGoal,
        });
      }

      if (repersonalizationTriggered) {
        await handleLearningPathRefresh(snapshot, result);
      } else if (changedKey === 'careerGoal') {
        setSavedCareerGoal(snapshot.careerGoal?.trim() || '');
      }

      // Show success notification
      toast.success(
        repersonalizationTriggered
          ? 'Learning path updated for your new career goal!'
          : `✨ ${endpoint.charAt(0).toUpperCase() + endpoint.slice(1)} updated!`,
        {
          duration: 2000,
          style: {
            background: '#10B981',
            color: '#FFFFFF'
          }
        }
      );

    } catch (error) {
      console.error('❌ Failed to save setting:', error);
      toast.error('Failed to save changes');
    } finally {
      setIsLoading(false);
    }
  };

  // 🔥 MANUAL SAVE ALL SETTINGS
  const handleSaveSettings = async () => {
    try {
      setIsLoading(true);
      console.log('💾 Manual save all settings triggered');

      // Save all categories
      const [profileResult] = await Promise.all([
        settingsAPI.updateProfile({
          name: settings.name,
          email: settings.email,
          phone: settings.phone,
          location: settings.location,
          preferred_language: settings.preferredLanguage,
          career_goal: settings.careerGoal,
          experience_level: settings.experienceLevel,
          learning_mode: settings.learningMode,
          father_name: settings.fatherName,
          mother_name: settings.motherName,
          date_of_birth: settings.dateOfBirth,
          nationality: settings.nationality,
          address: settings.address,
          linkedin: settings.linkedin,
          github: settings.github,
          website: settings.website,
          bio: settings.bio,
          degree: settings.degree,
          institution: settings.institution,
          graduation_year: settings.graduationYear,
          grade: settings.grade
        }),
        settingsAPI.updateNotifications({
          email_notifications: settings.emailNotifications,
          weekly_progress: settings.weeklyProgress,
          achievement_alerts: settings.achievementAlerts
        }),
        settingsAPI.updateLearning({
          passing_score: Number(settings.passingScore) || 70
        })
      ]);

      const repersonalizationTriggered = Boolean(profileResult?.data?.repersonalization?.triggered);
      updateUser({
        name: settings.name,
        email: settings.email,
        career_goal: settings.careerGoal,
        careerGoal: settings.careerGoal,
      });

      if (repersonalizationTriggered) {
        await handleLearningPathRefresh(settings, profileResult);
      } else {
        setSavedCareerGoal(settings.careerGoal?.trim() || '');
      }

      toast.success(
        repersonalizationTriggered
          ? '🎉 Settings saved and your learning path was refreshed!'
          : '🎉 All settings saved successfully!',
        {
          duration: 3000,
          style: {
            background: '#10B981',
            color: '#FFFFFF'
          }
        }
      );

    } catch (error) {
      console.error('❌ Failed to save all settings:', error);
      toast.error('Failed to save settings. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // 🔥 PASSWORD CHANGE HANDLER  
  const handleCareerGoalBlur = () => {
    const trimmedCareerGoal = settings.careerGoal?.trim() || '';
    const previousGoal = savedCareerGoal?.trim() || '';

    if (!trimmedCareerGoal || trimmedCareerGoal === previousGoal) {
      if (trimmedCareerGoal !== settings.careerGoal) {
        setSettings(prev => ({ ...prev, careerGoal: trimmedCareerGoal }));
      }
      return;
    }

    const snapshot = {
      ...settings,
      careerGoal: trimmedCareerGoal,
    };

    setSettings(snapshot);
    saveSettingToBackend('careerGoal', trimmedCareerGoal, snapshot);
  };

  const handlePasswordChange = async () => {
    if (!settings.currentPassword || !settings.newPassword) {
      toast.error('Please fill in all password fields.');
      return;
    }

    if (settings.newPassword !== settings.confirmPassword) {
      toast.error('New passwords do not match.');
      return;
    }

    if (settings.newPassword.length < 6) {
      toast.error('New password must be at least 6 characters long.');
      return;
    }

    try {
      setIsLoading(true);

      await settingsAPI.changePassword(
        settings.currentPassword, 
        settings.newPassword, 
        settings.confirmPassword
      );

      // Clear password fields
      setSettings(prev => ({
        ...prev,
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      }));

      toast.success('🔐 Password changed successfully!');
    } catch (error) {
      console.error('❌ Password change error:', error);
      toast.error('Failed to change password. Please check your current password.');
    } finally {
      setIsLoading(false);
    }
  };

  // 🔥 DELETE ACCOUNT HANDLER
  const handleDeleteAccount = () => {
    const confirmed = window.confirm(
      'Are you sure you want to delete your account? This action cannot be undone.'
    );

    if (confirmed) {
      const password = window.prompt('Please enter your password to confirm account deletion:');
      if (password) {
        settingsAPI.deleteAccount(password)
          .then(() => {
            toast.success('Account deleted successfully');
            logout();
          })
          .catch((error) => {
            console.error('❌ Delete account error:', error);
            toast.error('Failed to delete account');
          });
      }
    }
  };

  const tabs = [
    { id: 'personal-info', label: 'Profile', navLabel: 'Personal Information', icon: User },
    { id: 'notifications', label: 'Notifications', icon: Bell },
    { id: 'profile', label: 'Profile', icon: FileUser, to: '/profile' },
    { id: 'account', label: 'Account', icon: User }
  ];

  // Show loading screen while initial data is loading
  if (initialLoad) {
    return (
      <motion.div 
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center"
      >
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-purple-400 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <div className="text-white text-xl">Loading your settings...</div>
        </div>
      </motion.div>
    );
  }

  const renderProfileSettings = () => (
    <div className="space-y-8">
      {/* Basic Information */}
      <div className="bg-slate-800/50 rounded-lg p-6">
        <h3 className="text-xl font-semibold text-white mb-4 flex items-center">
          <User className="mr-2 h-5 w-5 text-black-400" />
          Basic Information
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Full Name *
            </label>
            <input
              type="text"
              value={settings.name}
              onChange={(e) => handleSettingChange('name', e.target.value)}
              className="w-full px-4 py-3 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-gray-400 focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all"
              placeholder="Enter your full name"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Email Address *
            </label>
            <input
              type="email"
              value={settings.email}
              onChange={(e) => handleSettingChange('email', e.target.value)}
              className="w-full px-4 py-3 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-gray-400 focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all"
              placeholder="Enter your email"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Phone Number
            </label>
            <input
              type="tel"
              value={settings.phone}
              onChange={(e) => handleSettingChange('phone', e.target.value)}
              className="w-full px-4 py-3 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-gray-400 focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all"
              placeholder="Enter your phone number"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Location
            </label>
            <input
              type="text"
              value={settings.location}
              onChange={(e) => handleSettingChange('location', e.target.value)}
              className="w-full px-4 py-3 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-gray-400 focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all"
              placeholder="Enter your location"
            />
          </div>
        </div>
      </div>

      {/* Learning Preferences
      <div className="bg-slate-800/50 rounded-lg p-6">
        <h3 className="text-xl font-semibold text-white mb-4 flex items-center">
          <Globe className="mr-2 h-5 w-5 text-black-400" />
          Learning Preferences
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Preferred Language
            </label>
            <select
              value={settings.preferredLanguage}
              onChange={(e) => handleSettingChange('preferredLanguage', e.target.value)}
              className="w-full px-4 py-3 bg-slate-700 border border-slate-600 rounded-lg text-white focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all"
            >
              {LANGUAGES?.map(lang => (
                <option key={lang.code} value={lang.code}>{lang.name}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Learning Mode
            </label>
            <select
              value={settings.learningMode}
              onChange={(e) => handleSettingChange('learningMode', e.target.value)}
              className="w-full px-4 py-3 bg-slate-700 border border-slate-600 rounded-lg text-white focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all"
            >
              {LEARNING_MODES?.map(mode => (
                <option key={mode.value} value={mode.value}>{mode.label}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Experience Level
            </label>
            <select
              value={settings.experienceLevel}
              onChange={(e) => handleSettingChange('experienceLevel', e.target.value)}
              className="w-full px-4 py-3 bg-slate-700 border border-slate-600 rounded-lg text-white focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all"
            >
              <option value="beginner">Beginner</option>
              <option value="intermediate">Intermediate</option>
              <option value="advanced">Advanced</option>
              <option value="expert">Expert</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Career Goal
            </label>
            <input
              type="text"
              value={settings.careerGoal}
              onChange={(e) => handleSettingChange('careerGoal', e.target.value)}
              onBlur={handleCareerGoalBlur}
              className="w-full px-4 py-3 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-gray-400 focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all"
              placeholder="What's your career goal?"
            />
          </div>
        </div>
      </div> */}



            <div className="bg-slate-800/50 rounded-lg p-6">
        <h3 className="text-xl font-semibold text-white mb-4 flex items-center">
          <Globe className="mr-2 h-5 w-5 text-black-400" />
          Learning Preferences
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Preferred Language
            </label>
            <input
              type="text"
              value={settings.preferredLanguage}
              disabled
              className="w-full px-4 py-3 bg-slate-900 border border-slate-700 rounded-lg text-white"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Learning Mode
            </label>
            <input
              type="text"
              value={settings.learningMode}
              disabled
              className="w-full px-4 py-3 bg-slate-900 border border-slate-700 rounded-lg text-white"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Experience Level
            </label>
            <input
              type="text"
              value={settings.experienceLevel}
              disabled
              className="w-full px-4 py-3 bg-slate-900 border border-slate-700 rounded-lg text-white"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Career Goal
            </label>
            <input
              type="text"
              value={settings.careerGoal}
              onChange={(e) => handleSettingChange('careerGoal', e.target.value)}
              onBlur={handleCareerGoalBlur}
              className="w-full px-4 py-3 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-gray-400 focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all"
              placeholder="What's your career goal?"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Assessment Passing Score
            </label>
            <input
              type="number"
              min="1"
              max="100"
              value={settings.passingScore}
              onChange={(e) => {
                const rawValue = e.target.value;
                const numericValue = rawValue === '' ? '' : Number(rawValue);
                handleSettingChange(
                  'passingScore',
                  rawValue === '' ? '' : Math.max(1, Math.min(100, numericValue || 0))
                );
              }}
              className="w-full px-4 py-3 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-gray-400 focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all"
              placeholder="Set your passing score"
            />
            <p className="text-xs text-gray-400 mt-2">
              Assessment results and next module unlocks use this score.
            </p>
          </div>
        </div>
      </div>

      {/* 🔥 PERSONAL INFORMATION SECTION */}
      <div className="bg-slate-800/50 rounded-lg p-6">
        <h3 className="text-xl font-semibold text-white mb-4">Personal Information</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Father's Name
            </label>
            <input
              type="text"
              value={settings.fatherName}
              onChange={(e) => handleSettingChange('fatherName', e.target.value)}
              className="w-full px-4 py-3 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-gray-400 focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all"
              placeholder="Enter father's name"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Mother's Name
            </label>
            <input
              type="text"
              value={settings.motherName}
              onChange={(e) => handleSettingChange('motherName', e.target.value)}
              className="w-full px-4 py-3 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-gray-400 focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all"
              placeholder="Enter mother's name"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Date of Birth
            </label>
            <input
              type="date"
              value={settings.dateOfBirth}
              onChange={(e) => handleSettingChange('dateOfBirth', e.target.value)}
              className="w-full px-4 py-3 bg-slate-700 border border-slate-600 rounded-lg text-white focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Nationality
            </label>
            <input
              type="text"
              value={settings.nationality}
              onChange={(e) => handleSettingChange('nationality', e.target.value)}
              className="w-full px-4 py-3 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-gray-400 focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all"
              placeholder="Enter nationality"
            />
          </div>

          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Address
            </label>
            <textarea
              value={settings.address}
              onChange={(e) => handleSettingChange('address', e.target.value)}
              className="w-full px-4 py-3 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-gray-400 focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all"
              rows="3"
              placeholder="Enter your address"
            />
          </div>
        </div>
      </div>

      {/* 🔥 SOCIAL LINKS SECTION */}
      <div className="bg-slate-800/50 rounded-lg p-6">
        <h3 className="text-xl font-semibold text-white mb-4">Social Links</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              LinkedIn Profile
            </label>
            <input
              type="url"
              value={settings.linkedin}
              onChange={(e) => handleSettingChange('linkedin', e.target.value)}
              className="w-full px-4 py-3 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-gray-400 focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all"
              placeholder="https://linkedin.com/in/yourprofile"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              GitHub Profile
            </label>
            <input
              type="url"
              value={settings.github}
              onChange={(e) => handleSettingChange('github', e.target.value)}
              className="w-full px-4 py-3 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-gray-400 focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all"
              placeholder="https://github.com/yourusername"
            />
          </div>

          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Website/Portfolio
            </label>
            <input
              type="url"
              value={settings.website}
              onChange={(e) => handleSettingChange('website', e.target.value)}
              className="w-full px-4 py-3 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-gray-400 focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all"
              placeholder="https://yourwebsite.com"
            />
          </div>

          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Bio
            </label>
            <textarea
              value={settings.bio}
              onChange={(e) => handleSettingChange('bio', e.target.value)}
              className="w-full px-4 py-3 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-gray-400 focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all"
              rows="4"
              placeholder="Tell us about yourself..."
            />
          </div>
        </div>
      </div>

      {/* 🔥 EDUCATION SECTION */}
      <div className="bg-slate-800/50 rounded-lg p-6">
        <h3 className="text-xl font-semibold text-white mb-4">Education</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Degree
            </label>
            <input
              type="text"
              value={settings.degree}
              onChange={(e) => handleSettingChange('degree', e.target.value)}
              className="w-full px-4 py-3 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-gray-400 focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all"
              placeholder="e.g., Bachelor of Computer Science"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Institution
            </label>
            <input
              type="text"
              value={settings.institution}
              onChange={(e) => handleSettingChange('institution', e.target.value)}
              className="w-full px-4 py-3 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-gray-400 focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all"
              placeholder="Institution name"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Graduation Year
            </label>
            <input
              type="number"
              value={settings.graduationYear}
              onChange={(e) => handleSettingChange('graduationYear', e.target.value)}
              className="w-full px-4 py-3 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-gray-400 focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all"
              placeholder="e.g., 2024"
              min="1950"
              max="2030"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Grade/GPA
            </label>
            <input
              type="text"
              value={settings.grade}
              onChange={(e) => handleSettingChange('grade', e.target.value)}
              className="w-full px-4 py-3 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-gray-400 focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all"
              placeholder="e.g., 3.8 GPA or First Class"
            />
          </div>
        </div>
      </div>
    </div>
  );

  const renderNotificationSettings = () => {
    const notificationSettings = [
      {
        key: 'emailNotifications',
        title: 'Email Notifications',
        description: 'Receive important updates via email',
        icon: Mail
      },
      {
        key: 'weeklyProgress',
        title: 'Weekly Progress Reports',
        description: 'Get weekly summaries of your learning progress',
        icon: Bell
      },
      {
        key: 'achievementAlerts',
        title: 'Achievement Alerts',
        description: 'Celebrate your milestones with instant notifications',
        icon: Volume2
      }
    ];

    return (
      <div className="space-y-4">
        <p className="text-black-300 mb-6">
          Customize how and when you want to receive notifications from AIVIONLearn.
        </p>
        {notificationSettings.map((setting) => {
          const IconComponent = setting.icon;
          return (
            <motion.div
              key={setting.key}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="flex items-center justify-between p-6 bg-slate-800/50 rounded-lg hover:bg-slate-800/70 transition-all"
            >
              <div className="flex items-center space-x-4">
                <div className="p-2 bg-purple-500/20 rounded-lg">
                  <IconComponent className="h-5 w-5 text-purple-400" />
                </div>
                <div>
                  <h4 className="text-white font-medium">{setting.title}</h4>
                  <p className="text-sm text-black-400">{setting.description}</p>
                </div>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={settings[setting.key]}
                  onChange={(e) => handleSettingChange(setting.key, e.target.checked)}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-slate-700 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-purple-800 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-purple-600"></div>
              </label>
            </motion.div>
          );
        })}
      </div>
    );
  };

  const renderAccountSettings = () => (
    <div className="space-y-6">
      {/* Password Change */}
      <div className="bg-slate-800/50 rounded-lg p-6">
        <h3 className="text-lg font-semibold text-white mb-4 flex items-center">
          <Shield className="mr-2 h-5 w-5 text-purple-400" />
          Change Password
        </h3>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Current Password
            </label>
            <div className="relative">
              <input
                type={showPasswords.current ? 'text' : 'password'}
                value={settings.currentPassword}
                onChange={(e) => handleSettingChange('currentPassword', e.target.value)}
                className="w-full px-4 py-3 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-gray-400 focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all pr-12"
                placeholder="Enter current password"
              />
              <button
                type="button"
                onClick={() => setShowPasswords(prev => ({ ...prev, current: !prev.current }))}
                className="absolute inset-y-0 right-0 pr-3 flex items-center hover:text-purple-400 transition-colors"
              >
                {showPasswords.current ? (
                  <EyeOff className="h-5 w-5 text-gray-400" />
                ) : (
                  <Eye className="h-5 w-5 text-gray-400" />
                )}
              </button>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              New Password
            </label>
            <div className="relative">
              <input
                type={showPasswords.new ? 'text' : 'password'}
                value={settings.newPassword}
                onChange={(e) => handleSettingChange('newPassword', e.target.value)}
                className="w-full px-4 py-3 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-gray-400 focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all pr-12"
                placeholder="Enter new password"
              />
              <button
                type="button"
                onClick={() => setShowPasswords(prev => ({ ...prev, new: !prev.new }))}
                className="absolute inset-y-0 right-0 pr-3 flex items-center hover:text-purple-400 transition-colors"
              >
                {showPasswords.new ? (
                  <EyeOff className="h-5 w-5 text-gray-400" />
                ) : (
                  <Eye className="h-5 w-5 text-gray-400" />
                )}
              </button>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Confirm New Password
            </label>
            <div className="relative">
              <input
                type={showPasswords.confirm ? 'text' : 'password'}
                value={settings.confirmPassword}
                onChange={(e) => handleSettingChange('confirmPassword', e.target.value)}
                className="w-full px-4 py-3 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-gray-400 focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all pr-12"
                placeholder="Confirm new password"
              />
              <button
                type="button"
                onClick={() => setShowPasswords(prev => ({ ...prev, confirm: !prev.confirm }))}
                className="absolute inset-y-0 right-0 pr-3 flex items-center hover:text-purple-400 transition-colors"
              >
                {showPasswords.confirm ? (
                  <EyeOff className="h-5 w-5 text-gray-400" />
                ) : (
                  <Eye className="h-5 w-5 text-gray-400" />
                )}
              </button>
            </div>
          </div>

          <Button
            onClick={handlePasswordChange}
            disabled={isLoading || !settings.currentPassword || !settings.newPassword || !settings.confirmPassword}
            className="w-full bg-purple-600 hover:bg-purple-700 disabled:opacity-50"
          >
            {isLoading ? 'Changing Password...' : 'Change Password'}
          </Button>
        </div>
      </div>

      {/* Danger Zone */}
      <div className="bg-red-900/20 border border-red-800 rounded-lg p-6">
        <h3 className="text-lg font-semibold text-red-400 mb-4 flex items-center">
          <Trash2 className="mr-2 h-5 w-5" />
          Danger Zone
        </h3>
        <div className="flex items-start space-x-4">
          <div className="flex-1">
            <h4 className="text-red-400 font-medium">Delete Account</h4>
            <p className="text-sm text-black-300 mt-1 mb-4">
              Permanently delete your account and all associated data. This action cannot be undone.
            </p>
            <Button
              variant="danger"
              onClick={handleDeleteAccount}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              Delete Account
            </Button>
          </div>
        </div>
      </div>
    </div>
  );

  const renderProfilePage = () => <ProfileSetupContent isEmbedded />;

  const renderTabContent = () => {
    switch (activeTab) {
      case 'personal-info':
        return renderProfileSettings();
      case 'notifications':
        return renderNotificationSettings();
      case 'profile':
        return renderProfilePage();
      case 'account':
        return renderAccountSettings();
      default:
        return location.pathname === '/profile' ? renderProfilePage() : renderProfileSettings();
    }
  };

  const currentTab = tabs.find(tab => tab.id === activeTab);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <motion.h1
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-4xl font-bold bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent"
          >
            Settings
          </motion.h1>
          <p className="text-gray-300 mt-2">
            Manage your account preferences and settings 😊✨
          </p>
          {isLoading && (
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="mt-3 flex items-center text-sm text-purple-400"
            >
              <div className="w-4 h-4 border-2 border-purple-400 border-t-transparent rounded-full animate-spin mr-2"></div>
              Saving changes...
            </motion.div>
          )}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Sidebar Navigation */}
          <div className="lg:col-span-1">
            <Card className="p-6 bg-slate-800/50 backdrop-blur-sm border-slate-700">
              <nav className="space-y-2">
                {tabs.map((tab) => {
                  const IconComponent = tab.icon;
                  const tabClasses = `w-full flex items-center space-x-3 px-4 py-3 rounded-lg text-left transition-all ${
                    activeTab === tab.id
                      ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white shadow-lg'
                      : 'text-black-300 hover:bg-slate-700/50 hover:text-white'
                  }`;

                  if (tab.to) {
                    return (
                      <Link key={tab.id} to={tab.to}>
                        <motion.div
                          whileHover={{ scale: 1.02 }}
                          whileTap={{ scale: 0.98 }}
                          className={tabClasses}
                        >
                          <IconComponent className="h-5 w-5" />
                          <span className="font-medium">{tab.navLabel || tab.label}</span>
                        </motion.div>
                      </Link>
                    );
                  }

                  return (
                    <motion.button
                      key={tab.id}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={() => setActiveTab(tab.id)}
                      className={tabClasses}
                    >
                      <IconComponent className="h-5 w-5" />
                      <span className="font-medium">{tab.navLabel || tab.label}</span>
                    </motion.button>
                  );
                })}
              </nav>
            </Card>
          </div>

          {/* Main Content */}
          <div className="lg:col-span-3">
            <Card className="p-8 bg-slate-800/30 backdrop-blur-sm border-slate-700">
              <div className="mb-8">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    {currentTab && <currentTab.icon className="h-6 w-6 text-purple-400" />}
                    <h2 className="text-2xl font-semibold text-white">
                      {currentTab?.label} Settings
                    </h2>
                  </div>
                  <Button
                    onClick={handleSaveSettings}
                    disabled={isLoading}
                    className="flex items-center space-x-2 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700"
                  >
                    <Save className="h-4 w-4" />
                    <span>{isLoading ? 'Saving...' : 'Save All'}</span>
                  </Button>
                </div>
                <p className="text-black-400 mt-2">
                  Configure your {currentTab?.label.toLowerCase()} preferences. Changes are automatically saved.
                </p>
              </div>

              <motion.div
                key={activeTab}
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.3 }}
              >
                {renderTabContent()}
              </motion.div>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SettingsPage;
